import { useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';
import { t } from '../../i18n/I18n';

type Props = { src: string; alt: string; className?: string; loading?: 'eager' | 'lazy'; retry?: boolean };
export function WorkspaceImage(props: Props) {
  const [attempt, setAttempt] = useState(0);
  return <ImageRequest key={props.src + ':' + attempt} {...props} onRetry={() => setAttempt(value => value + 1)} />;
}

function ImageRequest({ src, alt, className = '', loading = 'eager', retry = false, onRetry }: Props & { onRetry: () => void }) {
  const image = useRef<HTMLImageElement>(null);
  const generation = useRef(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const reveal = async (node: HTMLImageElement) => {
    const token = generation.current;
    try {
      await node.decode();
      if (token === generation.current && image.current === node && node.naturalWidth > 0) setStatus('ready');
    } catch {
      if (token === generation.current && image.current === node) setStatus('error');
    }
  };
  useEffect(() => {
    const node = image.current;
    if (node?.complete) {
      if (node.naturalWidth > 0) void reveal(node);
      else setStatus('error');
    }
    return () => { generation.current += 1; };
  }, []);
  return <span className={'ws-media ' + className} data-media-state={status} aria-busy={status === 'loading'}>
    <img ref={image} src={src} alt={alt} loading={loading} decoding="async" onLoad={event => { void reveal(event.currentTarget); }} onError={() => { generation.current += 1; setStatus('error'); }} />
    {status === 'loading' && <span className="ws-media-placeholder" aria-hidden="true"><Icon name="gallery" size={24} /></span>}
    {status === 'error' && <span className="ws-media-error"><Icon name="gallery" size={24} /><span>{t('#HomeImageFailed')}</span>{retry && <button type="button" className="ws-button" onClick={onRetry}>{t('#HomeImageRetry')}</button>}</span>}
  </span>;
}
